/**
 * cart-agenda.js
 * Asset de Shopify — adaptación del agendamiento original.
 *
 * CAMBIOS vs. versión anterior:
 *  1. Todo el código vive en el namespace `CartAgenda` (sin globales sueltas).
 *  2. `middlewareBaseUrl` se lee desde window.CartAgendaConfig.
 *  3. Sin jQuery ($) → fetch() nativo.
 *  4. Módulo de direcciones delegado completamente a window.AddressManager.
 *     - `openAddressModal()` → AddressManager.open()
 *     - La dirección seleccionada se lee con AddressManager.getSelected()
 *     - CartAgenda.onAddressSelect() sincroniza la UI del chip y el flujo.
 *  5. El modal de dirección inline y todo su código fue eliminado de aquí.
 *     Vive ahora en assets/address-manager.js.
 *  6. SweetAlert2 (Swal) debe cargarse antes de este script.
 *  7. address-manager.js debe cargarse ANTES que cart-agenda.js.
 *
 * FIXES:
 *  - El wrap de domicilio (#sched-domicilio-wrap) solo se muestra después
 *    de que el usuario selecciona un horario (selectHorario), nunca antes.
 *  - onAddressSelect() solo renderiza el chip si el wrap ya está visible
 *    Y la ciudad de la dirección coincide con la ciudad del agendamiento.
 *  - init() ya no pre-renderiza el chip al cargar la página.
 */

(function () {
  'use strict';

  /* ─── Helpers ──────────────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  const cfg = () => window.CartAgendaConfig || {};

  /* ─── Estado interno ────────────────────────────────────────── */
  let fechaAgenda = '';
  let horaAgenda  = '';
  let sedeAgenda  = '';
  // Domicilio: la dirección cuenta solo si el cliente la escogió o la creó en esta visita.
  // La que el navegador recuerda de otra compra no se usa sin confirmarla.
  // B59: se guarda el id de la dirección que el cliente escogió (clic en la tarjeta, «Confirmar»
  // o «Guardar dirección»). Si el gestor cambia la selección por su cuenta (al borrar la escogida
  // o al sincronizar al iniciar sesión), esa otra dirección no queda confirmada.
  let idDireccionConfirmada = null;
  function direccionEscogida() {
    const addr = window.AddressManager?.getSelected();
    return addr && idDireccionConfirmada !== null && addr.id === idDireccionConfirmada ? addr : null;
  }
  document.addEventListener('click', function (e) {
    const t = e.target;
    if (!t || !t.closest) return;
    const card = t.closest('.vtl-addr-card');
    if (card && !t.closest('[data-action]')) { idDireccionConfirmada = card.dataset.id; return; }
    if (t.closest('#vtl-addr-confirm')) {
      const addr = window.AddressManager?.getSelected();
      if (addr) idDireccionConfirmada = addr.id;
    }
  }, true);
  // B74: solo una dirección nueva que sí se guardó queda escogida (no un envío que falló ni una edición)
  document.addEventListener('vtl:direccion-creada', function (e) {
    if (e.detail && e.detail.id) idDireccionConfirmada = e.detail.id;
  });

  const monthsName        = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const weekdaysNameShort = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

  /* ─── Utilidades ────────────────────────────────────────────── */
  const pad = (n, size) => String(n).padStart(size, '0');

  /* M10: si algún camino olvida ocultar el cargando (se vio una vez tras agregar
     un examen sin recargar, sin reproducirse), a los 25 s se oculta y se avisa.
     Las consultas tienen su propio límite de 15 s, así que solo actúa ante un fallo. */
  let _loadingTimer = null;
  function loading(state) {
    const el = $('#loading-overlay');
    if (!el) return;
    el.style.display = state === 'show' ? 'flex' : 'none';
    clearTimeout(_loadingTimer);
    if (state === 'show') {
      _loadingTimer = setTimeout(() => {
        if (el.style.display === 'none') return;
        el.style.display = 'none';
        console.warn('[agenda] el cargando superó 25 s; se oculta');
        if (window.vtlMetrica) window.vtlMetrica('paso_2_error', { step: 2, detail: 'agenda_lenta' });
        // P5: no reemplazar un aviso abierto (p. ej. la confirmación del domicilio):
        // Swal.fire cerraría el anterior y se perdería lo que el cliente iba a confirmar
        if (typeof Swal !== 'undefined' && Swal.isVisible()) return;
        ventanaConfirma({
          titulo:       'La agenda tardó demasiado',
          texto:        'Vuelve a elegir la sede o la fecha para intentarlo de nuevo.',
          icon:         'info',
          confirmLabel: 'Entendido',
        });
      }, 25000);
    }
  }

  /* C4: si el middleware falla o tarda, el cargando quedaba girando para
     siempre. Toda consulta de agenda tiene tiempo límite, y ante un error se
     oculta el cargando y se ofrece reintentar o escribir por WhatsApp. */
  const FETCH_TIMEOUT_MS = 15000;
  // V15-B12: la verificación del turno corre con el cliente esperando en «Confirmar y pagar»
  const TURNO_TIMEOUT_MS = 10000;
  // M1 (/probar 26-10 17:24): tope total para mover la cita a otra media hora de la franja; sin él,
  // con Shopify lento, «Confirmando tu horario…» podía durar más de un minuto
  const TURNO_MOVER_MAX_MS = 20000;

  function fetchConTimeout(url, opts = {}, ms = FETCH_TIMEOUT_MS) {
    const ctrl  = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { ...opts, signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .finally(() => clearTimeout(timer));
  }

  /* V15-B1: cupos por bloque de la agenda. 0, null o un valor que no es número cuentan como 0
     (la sede no tiene cupos configurados); antes `|| 1` los volvía 1 cupo en algunos sitios. */
  function _cupos(valor) {
    const n = Number(valor);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /* '06:30' → 390. Acepta '6:30'. NaN si no es una hora. */
  function _minutos(h) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(h || '').trim());
    return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
  }

  const ICONO_DOMICILIO = 'https://cdn.shopify.com/s/files/1/0616/2137/1132/files/ICONO_domicilio.svg?v=1724705171';

  /* V15-B6: el middleware dice «sin agenda» de dos formas (BACK ajax/consultarcalendar.php) y
     ninguna es una falla de conexión:
      - bloquesHorario 'No existen agendas Configuradass' (:312-331): el CTS no tiene horarios.
      - { error: 1, curl_errno: 0 } (:74-84): ValidarTipoCalendario respondió vacío, la ciudad no
        tiene calendario (Abejorral en el CTS de pruebas). Con una falla real curl_errno no es 0. */
  function _sinAgendaConfigurada(calRes) {
    if (!calRes) return false;
    if (calRes.bloquesHorario === 'No existen agendas Configuradass') return true;
    return calRes.error === 1 && calRes.curl_errno === 0;
  }

  /* V15-B6: en domicilio, el lugar se nombra como el selector donde se escogió */
  function _lugarDomicilio(bandera) {
    if (bandera === 'CP') return 'ciudad';
    if (bandera === 'CS3' || bandera === 'localidades') return 'localidad';
    return 'zona';
  }

  /* V15-B6: sin agenda, sin cupos o sin cargo de domicilio en esa ciudad, zona o localidad. Antes
     salía el texto de la sede («No existen agendas configuradas para esta sede.») o una falla de
     conexión. Misma línea gráfica que «Domicilio no disponible» de continueResumen.
     sinCargo: la zona no tiene cargo de domicilio; se mide igual que cuando lo decía «Agendar». */
  function _avisoSinDomicilio(lugar, sinCargo) {
    if (sinCargo && window.vtlMetrica) window.vtlMetrica('cita_error', { step: 2, detail: 'sin_domicilio_zona' });
    return ventanaConfirma({
      titulo:       'Domicilio no disponible',
      texto:        `Por ahora no tenemos servicio a domicilio en esta ${lugar}. Escoge otra ${lugar} o agenda tu cita en una de nuestras sedes.`,
      iconImg:      ICONO_DOMICILIO,
      confirmLabel: 'Entendido',
    });
  }

  /* El cargo de domicilio que usará «Agendar» para esa localidad (mismo JSON que continueResumen) */
  function _hayCargoDomicilio(codAthenea) {
    let cargos = {};
    try { cargos = JSON.parse($('#products_cargos_domicilio')?.innerHTML || '{}'); } catch (e) { /* sin cargos */ }
    return `P${codAthenea || ''}` in cargos;
  }

  function errorAgenda(reintentar) {
    loading('hide');
    if (window.vtlMetrica) window.vtlMetrica('paso_2_error', { step: 2, detail: 'consulta_agenda' });
    const wa = window.VTL_WHATSAPP
      ? `https://api.whatsapp.com/send/?phone=${window.VTL_WHATSAPP}&text=${encodeURIComponent('Hola, no pude agendar mi cita en la web')}`
      : '';
    Swal.fire({
      icon: 'warning',
      html: `
        <div style="display:flex;flex-direction:column;align-items:center;text-align:center">
          <strong style="font-size:18px;color:#e71578;margin-bottom:8px">No pudimos consultar la agenda</strong>
          <div>La conexión tardó demasiado o falló. Intenta de nuevo${wa ? ' o agenda con nosotros por WhatsApp' : ''}.</div>
        </div>`,
      footer: wa ? `<a href="${wa}" target="_blank" rel="noopener" style="color:#1e7a4c;font-weight:600">Agendar por WhatsApp</a>` : '',
      showConfirmButton:  true,
      showCloseButton:    true,
      allowOutsideClick:  false,
      confirmButtonText:  'Reintentar',
      confirmButtonColor: '#e71578',
    }).then(r => { if (r.isConfirmed && reintentar) reintentar(); });
  }

  function moneyFormat(cents) {
    return '$' + (cents / 100).toLocaleString('es-CO');
  }

  function getMetodoTitle(bandera) {
    const map = { CP: 'Ciudad', CS1: 'Sede', CS2: 'Zona', CS3: 'Localidad' };
    return map[bandera] || bandera;
  }

  /* ─── Normalización de strings (elimina tildes, mayúsculas) ─── */
  function _normalizeStr(str) {
    return (str || '').toUpperCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  /**
   * C5: compara la ciudad de la dirección con la del agendamiento sin exigir
   * el texto exacto: ignora puntuación y sufijos como "D.C." o "Distrito
   * Capital", y acepta que un nombre contenga al otro ("Santa Fe de Bogotá").
   */
  function _claveCiudad(str) {
    return _normalizeStr(str)
      .replace(/[^A-Z0-9 ]/g, ' ')
      .replace(/\b(D ?C|D ?T ?Y ?C|DISTRITO CAPITAL|DISTRITO)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function _ciudadCoincide(addrCity, ciudadAgenda) {
    const a = _claveCiudad(addrCity);
    const b = _claveCiudad(ciudadAgenda);
    if (!a || !b) return false;
    if (a === b) return true;
    const corta = a.length < b.length ? a : b;
    const larga = a.length < b.length ? b : a;
    return corta.length >= 4 && (' ' + larga + ' ').includes(' ' + corta + ' ');
  }

  /** C5: antes el botón "Agendar" se ocultaba sin explicar por qué. */
  function _avisoCiudad(visible) {
    const btn = $('#continue-resumen');
    if (!btn) return;
    let aviso = document.getElementById('vtl-aviso-ciudad');
    if (!visible) { if (aviso) aviso.hidden = true; return; }
    if (!aviso) {
      aviso = document.createElement('p');
      aviso.id = 'vtl-aviso-ciudad';
      aviso.setAttribute('role', 'alert');
      aviso.style.cssText = 'margin:8px 0 0;padding:10px 12px;border-radius:8px;background:#fdecf4;color:#c2156a;font-size:.86rem;line-height:1.4';
      btn.insertAdjacentElement('afterend', aviso);
    }
    aviso.textContent = 'La dirección seleccionada no está en la ciudad de la cita. Elige o agrega una dirección en esa ciudad para continuar.';
    aviso.hidden = false;
  }

  /**
   * Lee la ciudad activa del select CP del agendamiento.
   * Retorna el nombre normalizado (sin tildes, mayúsculas) o null.
   */
  function _getCiudadAgendamiento() {
    const nombre = _nombreCiudadAgendamiento();
    return nombre ? _normalizeStr(nombre) : null;
  }

  /** Nombre de la ciudad del select CP tal como sale en la lista, o '' si no hay. */
  function _nombreCiudadAgendamiento() {
    const selCP = document.querySelector('.agendamiento-select[bandera="CP"] select');
    if (!selCP || selCP.selectedIndex <= 0) return '';
    const opt    = selCP.selectedOptions[0];
    const nombre = (opt.getAttribute('nombresede') || opt.textContent || '').trim();
    return !nombre || nombre.startsWith('Selecciona') ? '' : nombre;
  }

  /* ─── Acordeón ──────────────────────────────────────────────── */
  function initAccordion() {
    $$('[data-accordion-trigger]').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item   = trigger.closest('.cart-accordion__item');
        const isOpen = item.classList.contains('cart-accordion__item--open');

        $$('.cart-accordion__item').forEach(el => {
          el.classList.remove('cart-accordion__item--open');
          el.querySelector('[data-accordion-trigger]')?.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          item.classList.add('cart-accordion__item--open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════
     MÓDULO DE DIRECCIONES — Delegado a AddressManager
     ═══════════════════════════════════════════════════════════════

     CartAgenda ya NO gestiona el CRUD de direcciones ni monta ningún
     modal propio. Toda esa responsabilidad vive en address-manager.js.

     CartAgenda sólo:
      1. Abre el modal cuando el flujo de domicilio lo requiere.
      2. Escucha el evento de selección para actualizar la UI del chip
         y re-evaluar si el botón "Agendar" puede habilitarse.
         IMPORTANTE: solo actualiza el chip si el wrap ya está visible,
         es decir, si el usuario ya seleccionó un horario, Y la ciudad
         de la dirección coincide con la ciudad del agendamiento.
      3. Lee la dirección seleccionada en continueResumen().
  */

  /**
   * Abre el modal de AddressManager y, si no hay ninguna dirección
   * todavía, lo abre automáticamente al mostrar la sección de domicilio.
   */
  function openAddressModal() {
    if (typeof window.AddressManager === 'undefined') {
      console.warn('[CartAgenda] AddressManager no disponible.');
      return;
    }
    window.AddressManager.open();
  }

  /**
   * Callback que se suscribe a AddressManager.onSelect().
   * Se ejecuta cada vez que el usuario selecciona o crea una dirección.
   *
   * FIX 1: Solo actualiza el chip si el wrap de domicilio ya está visible
   * (es decir, si el usuario ya seleccionó un horario). Esto evita que
   * el bloque de dirección aparezca antes de que el flujo lo requiera.
   *
   * FIX 2: Verifica que la ciudad de la dirección coincida con la ciudad
   * del agendamiento activo. Si no coincide, no muestra el chip y fuerza
   * al usuario a seleccionar una dirección compatible.
   *
   * @param {object|null} addr - Dirección canónica seleccionada
   */
  function onAddressSelect(addr) {
    addr = direccionEscogida();
    const domicilioWrap = $('#sched-domicilio-wrap');

    // Solo renderiza el chip si el wrap ya fue revelado por selectHorario()
    if (domicilioWrap && !domicilioWrap.classList.contains('hidden')) {
      if (addr) {
        const ciudadAgenda = _getCiudadAgendamiento();

        // Si hay ciudad de agendamiento activa y no coincide: no mostrar chip
        if (ciudadAgenda && !_ciudadCoincide(addr.city, ciudadAgenda)) {
          _renderAddressChip(null);
          beforeContinue();
          return;
        }
      }
      _renderAddressChip(addr);
    }
    beforeContinue();
  }

  /**
   * Muestra u oculta el chip de dirección seleccionada en la UI
   * del snippet de agendamiento.
   */
  function _renderAddressChip(addr) {
    const wrap      = $('#sched-domicilio-wrap');
    const chip      = $('#sched-selected-address');
    const chipText  = $('#sched-selected-address-text');
    const addBtn    = $('#boton_abrir_modal');
    if (!wrap || !chip || !chipText) return;

    if (addr) {
      const label = [addr.label, addr.address1, addr.address2, addr.city, addr.province]
        .filter(Boolean).join(', ');
      chipText.textContent = label;
      chip.classList.remove('hidden');
      addBtn && addBtn.classList.add('hidden');
    } else {
      chip.classList.add('hidden');
      addBtn && addBtn.classList.remove('hidden');
    }
  }

  /* ─── Calendario: navegación días ──────────────────────────── */
  function daysNextPage() {
    const optionDays = [
      ...document.querySelectorAll('#day-selector .date-picker')
    ].filter(el => el.getAttribute('date'));
    if (!optionDays.length) return;
    const lastDate =
      optionDays[optionDays.length - 1].getAttribute('date');
    const lastDateObj = new Date(lastDate + 'T00:00:00');
    lastDateObj.setDate(lastDateObj.getDate() + 1);
    // Tope: el último día del tercer mes del selector
    if (lastDateObj > _ultimoDiaAgendable()) return;
    fillDaysCalendar(lastDateObj);
  }

  function daysBackPage() {
    const optionDays = [
      ...document.querySelectorAll('#day-selector .date-picker')
    ].filter(el => el.getAttribute('date'));
    if (!optionDays.length) return;
    const firstDate = optionDays[0].getAttribute('date');
    const firstDateObj = new Date(firstDate + 'T00:00:00');
    firstDateObj.setDate(firstDateObj.getDate() - 7);
    // Tope: la semana no empieza antes de mañana, igual que el selector de mes
    const manana = _manana();
    if (firstDateObj < manana) {
      if (new Date(firstDate + 'T00:00:00') <= manana) return;
      firstDateObj.setTime(manana.getTime());
    }
    fillDaysCalendar(firstDateObj);
  }

  /* Mañana a las 00:00: el primer día que se puede agendar */
  function _manana() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return d;
  }

  /* Último día que ofrece el selector de mes: fin del tercer mes contando el de mañana */
  function _ultimoDiaAgendable() {
    const m = _manana();
    return new Date(m.getFullYear(), m.getMonth() + 3, 0);
  }

  /* Al cambiar de semana o de mes se suelta el día y la hora escogidos: antes «Agendar»
     seguía activo con una fecha que ya no se veía. La jornada se conserva (P1). */
  function _soltarDiaEscogido() {
    $$('#day-selector .date-picker').forEach(o => o.classList.remove('selected'));
    $('#agendamiento-hour-select')?.classList.add('hidden');
    ['#horario-container', '#horario-container-domi'].forEach(sel => {
      const c = $(sel);
      if (c) { c.innerHTML = ''; c.classList.add('hidden'); }
    });
    const avisoSinCupo = $('#sched-sin-cupo');
    if (avisoSinCupo) avisoSinCupo.hidden = true;
    if ($('#date-selected'))        $('#date-selected').value        = '';
    if ($('#horario-seleccionado')) $('#horario-seleccionado').value = '';
    fechaAgenda = '';
    horaAgenda  = '';
    $('div[finish-select]') && ($('div[finish-select]').style.display = 'none');
    $('#continue-resumen')?.setAttribute('disabled', 'disabled');
    $('#sched-domicilio-wrap')?.classList.add('hidden');
    _renderAddressChip(null);
  }

  /* ─── Selección de fecha ────────────────────────────────────── */
  function selectDate(dateSelected) {
    _resetChecks();
    if (dateSelected.classList.contains('disabled')) return;

    const dateValue = dateSelected.getAttribute('date');
    if (!dateValue) return;

    // P1: la jornada se lee antes de limpiarla; antes se quitaba primero y la rama de abajo
    // («si ya tenía jornada, recarga los horarios del nuevo día») nunca corría
    const jornadaPrevia = $('.title-control .control label.selected')?.getAttribute('for') || null;
    $$('#day-selector .date-picker').forEach(o => o.classList.remove('selected'));
    $('.title-control .control label.selected')?.classList.remove('selected');
    dateSelected.classList.add('selected');

    // Limpia horario previo al cambiar de día (y el aviso de M4 del día anterior)
    const avisoSinCupo = $('#sched-sin-cupo');
    if (avisoSinCupo) avisoSinCupo.hidden = true;
    $('#horario-container')?.classList.add('hidden');
    $('#horario-container-domi')?.classList.add('hidden');
    if ($('#horario-container'))      $('#horario-container').innerHTML      = '';
    if ($('#horario-container-domi')) $('#horario-container-domi').innerHTML = '';

    // Al cambiar de día, oculta la sección de domicilio y limpia el chip
    // (el usuario debe volver a seleccionar horario para que reaparezca)
    const domicilioWrap = $('#sched-domicilio-wrap');
    domicilioWrap?.classList.add('hidden');
    _renderAddressChip(null);

    // Oculta el footer hasta que se seleccione un horario nuevo
    const footer = $('div[finish-select]');
    if (footer) footer.style.display = 'none';

    $('#agendamiento-hour-select')?.classList.remove('hidden');
    const labelPrevia = jornadaPrevia && $(`.title-control .control label[for="${jornadaPrevia}"]`);
    if (labelPrevia) {
      // Si ya tenía jornada elegida, la conserva y recarga los horarios para el nuevo día
      labelPrevia.classList.add('selected');
      fillHorarios(jornadaPrevia);
    }
    // Sin jornada: se ven las pestañas pero NO el select de horarios hasta elegir una
  }

  /* ─── Horarios ──────────────────────────────────────────────── */
  function fillHorarios(tipoHorarios) {
    const selectedDate = $('#day-selector .date-picker.col-1.selected')?.getAttribute('date');
    if (!selectedDate) return;

    if ($('#date-selected')) $('#date-selected').value = selectedDate;
    if ($('#horario-container')) $('#horario-container').innerHTML = '';

    const html    = _opcionesHorarios(selectedDate, tipoHorarios);
    const libres  = _contarLibres(html);
    // M4: sin cupo en esa jornada se ofrece la otra jornada o el siguiente día con cupo
    const alternativa = _sugerirAlternativa(selectedDate, tipoHorarios, libres);
    if (!libres && window.vtlMetrica) {
      window.vtlMetrica('sin_cupos', { step: 2, detail: 'dia', data: { metodo: (window.dataMetodoMuestra || {}).metodo || null, jornada: tipoHorarios === 'horarios-mañana' ? 'manana' : 'tarde', alternativa: alternativa } });
    }
    const container = $(dataMetodoMuestra.metodo === 'domicilio' ? '#horario-container-domi' : '#horario-container');
    if (container) {
      container.innerHTML = _sinHorariosLibres(html, libres);
      container.classList.remove('hidden');
    }
    $('div[finish-select]') && ($('div[finish-select]').style.display = 'none');
  }

  /* M4: opciones de una fecha y jornada sin tocar la pantalla. Sirve para pintar el select y
     para buscar alternativas cuando la jornada escogida no tiene cupo. */
  function _opcionesHorarios(fecha, tipoHorarios) {
    const blockDates     = JSON.parse($('#datesBlocked')?.innerHTML || '[]');
    const bloquesHorario        = JSON.parse($('#bloquesHorario')?.innerHTML      || '{}');
    // V15-B1: misma cuenta que verificarTurno (0 o null = sin cupos)
    const cuposPorBloques       = _cupos(JSON.parse($('#cuposPorBloques')?.innerHTML || '1'));
    const almuerzoPorDiaSemanal = JSON.parse($('#almuerzoPorDiaSemanal')?.innerHTML || '{}');

    /* Conteo de fechas bloqueadas */
    const fechasRepetidas = {};
    blockDates.forEach(f => { fechasRepetidas[f] = (fechasRepetidas[f] || 0) + 1; });

    /* H9: el middleware (consultarcalendar.php) numera D0 = lunes … D6 = domingo,
       y getDay() da 0 = domingo. Se convierte explícitamente. Antes de este flujo
       se usaba new Date(fecha).getDay() en UTC, que en Colombia cae el día
       anterior y por casualidad daba el mismo índice; leer la fecha como hora
       local sin convertir le mostraba al domingo los horarios del lunes. */
    const weekdaySelected = (new Date(fecha + 'T00:00:00').getDay() + 6) % 7;
    let horariosDia = bloquesHorario['D' + weekdaySelected] || [];
    const almuerzosDia = almuerzoPorDiaSemanal['D' + weekdaySelected] || [];

    /* Filtro horario nocturno para domicilio tipo_servicio==3 */
    if (Number($('#tipo_servicio')?.value) === 3) {
      horariosDia = horariosDia.filter(h => !_bloqueadaDeNoche(fecha, h));
    }

    /* ── Modo DOMICILIO: franjas de 2 horas ─── */
    return dataMetodoMuestra.metodo === 'domicilio'
      ? _htmlHorariosDomicilio(horariosDia, tipoHorarios, fecha, weekdaySelected, fechasRepetidas, cuposPorBloques)
      : _htmlHorariosSede(horariosDia, tipoHorarios, fecha, fechasRepetidas, cuposPorBloques, almuerzosDia);
  }

  function _contarLibres(html) {
    return (html.match(/<option (?!selected)(?!disabled)/g) || []).length;
  }

  /* Si ningún horario quedó libre (todos ocupados, almuerzo o la sede sin
     cupos configurados), el encabezado lo dice en vez de mostrar una lista
     entera deshabilitada sin explicación. */
  function _sinHorariosLibres(html, libres) {
    return libres > 0 ? html
      : html.replace('<option selected disabled>Selecciona un Horario</option>',
                     '<option selected disabled>Sin horarios en esta jornada</option>');
  }

  /* M4: en la corrida de medición la gente se quedaba en «Mañana o tarde» al escoger un día sin
     cupo en esa jornada. Se ofrece, con un botón, la otra jornada del mismo día o el siguiente
     día de la semana visible que sí tenga cupo. Devuelve qué se ofreció (para la medición). */
  function _sugerirAlternativa(fecha, tipoHorarios, libres) {
    let caja = $('#sched-sin-cupo');
    if (!caja) {
      caja = document.createElement('div');
      caja.id = 'sched-sin-cupo';
      caja.className = 'sched-sin-cupo';
      caja.setAttribute('role', 'status');
      $('#agendamiento-hour-select')?.appendChild(caja);
    }
    caja.hidden = true;
    caja.innerHTML = '';
    if (libres) return null;

    const actual    = tipoHorarios === 'horarios-mañana' ? 'mañana' : 'tarde';
    const otra      = tipoHorarios === 'horarios-mañana' ? 'horarios-tarde' : 'horarios-mañana';
    const otraTexto = otra === 'horarios-mañana' ? 'mañana' : 'tarde';
    const mostrar   = (texto, boton, accion) => {
      caja.innerHTML = `<p class="sched-sin-cupo__texto">${texto}</p>` +
        (boton ? `<button type="button" class="sched-sin-cupo__btn">${boton}</button>` : '');
      const btn = caja.querySelector('button');
      if (btn) btn.addEventListener('click', accion);
      caja.hidden = false;
    };

    if (_contarLibres(_opcionesHorarios(fecha, otra))) {
      mostrar(`Este día no tiene cupo en la ${actual}, pero sí en la ${otraTexto}.`, `Ver horarios de la ${otraTexto}`, () => {
        const label = $(`.title-control .control label[for="${otra}"]`);
        if (label) selectThisHorarios(label);
      });
      return 'otra_jornada';
    }

    const dia = $$('#day-selector .date-picker.col-1[date]:not(.disabled):not(.hidden)')
      .filter(d => d.getAttribute('date') > fecha)
      .find(d => _contarLibres(_opcionesHorarios(d.getAttribute('date'), tipoHorarios)));
    if (dia) {
      const nombre = fmtFechaLarga(dia.getAttribute('date'));
      // selectDate conserva la jornada (P1), así que basta con escoger el día
      mostrar(`Este día no tiene cupo en la ${actual}. El ${nombre} sí tiene.`, `Ver el ${nombre}`, () => dia.click());
      return 'otro_dia';
    }

    // En la última semana agendable la flecha está apagada: no se ofrece la semana siguiente
    const haySiguiente = !$('.control-day.next')?.disabled;
    mostrar(`No quedan cupos en la ${actual} esta semana. Prueba la ${otraTexto}${haySiguiente ? ' o la semana siguiente' : ''}.`);
    return 'ninguna';
  }

  function _htmlHorariosSede(horarios, tipoHorarios, selectedDate, fechasRepetidas, cuposPorBloques, almuerzosDia) {
    let html = '<option selected disabled>Selecciona un Horario</option>';
    horarios.forEach(horario => {
      const hora = Number(horario.split(':')[0]);
      let bloqueCondicion = hora >= 12;
      if (tipoHorarios === 'horarios-mañana') bloqueCondicion = hora <= 11;

      if (bloqueCondicion) {
        const key = `${selectedDate} ${horario}`;
        let disabled = '';
        if ((fechasRepetidas[key] || 0) >= cuposPorBloques) disabled = 'disabled';
        if (almuerzosDia.includes(horario)) disabled = 'disabled';
        html += `<option ${disabled} horario="${horario}">${horario}</option>`;
      }
    });
    return html;
  }

  function _htmlHorariosDomicilio(horarios, tipoHorarios, selectedDate, weekdaySelected, fechasRepetidas, cuposPorBloques) {
    /* Filtra bloque mañana/tarde */
    const optArray = horarios.filter(h => {
      const hora = Number(h.split(':')[0]);
      return tipoHorarios === 'horarios-mañana' ? hora <= 12 : hora >= 12;
    });

    let html = '<option selected disabled>Selecciona un Horario</option>';

    for (let i = 0; i < optArray.length - 1; i++) {
      const horarioInicio = optArray[i].endsWith(':00') ? optArray[i] : null;
      if (!horarioInicio) continue;

      const startHour = new Date(`1970-01-01T${horarioInicio}:00`);
      let horarioFin  = null;

      for (let j = i + 1; j < optArray.length; j++) {
        const endHour = new Date(`1970-01-01T${optArray[j]}:00`);
        const diffH   = (endHour - startHour) / 3600000;
        const diffHH  = (endHour - startHour) / 1800000;

        // 5 = sábado en la numeración del middleware (igual que antes de este flujo)
        if (weekdaySelected === 5 && diffHH === 3 && tipoHorarios === 'horarios-tarde') {
          horarioFin = optArray[j]; break;
        }
        if (diffH === 2) { horarioFin = optArray[j]; break; }
      }

      if (!horarioFin) continue;

      let disabled            = '';
      let primeraHoraLibre    = '';
      let allOccupied         = true;
      let cursor = new Date(startHour);

      // B85: la franja «06:00 a 08:00» empieza entre 06:00 y 07:30; con <= se tomaba 08:00, que
      // ya es la franja siguiente, y la cita quedaba fuera de la franja que vio el cliente
      while (cursor < new Date(`1970-01-01T${horarioFin}:00`)) {
        const key = `${selectedDate} ${cursor.toTimeString().slice(0,5)}`;
        if (!((fechasRepetidas[key] || 0) >= cuposPorBloques)) {
          primeraHoraLibre = cursor.toTimeString().slice(0,5);
          allOccupied = false;
          break;
        }
        cursor.setMinutes(cursor.getMinutes() + 30);
      }

      if (allOccupied) disabled = 'disabled';
      html += `<option ${disabled} value="${primeraHoraLibre}">${horarioInicio} a ${horarioFin}</option>`;
    }

    return html;
  }

  let selectedHorario = '';
  let selectedFranja  = ''; // B94: texto de la opción («07:00 a 09:00»); la hora guardada puede ser otra de la franja
  let sede_id         = '';
  let sedeName        = '';
  let date_selected   = '';
  let tipo_servicio   = '';

  /* B94: «franja de 07:00 a 09:00» o, si la cita queda más tarde dentro de ella,
     «franja de 07:00 a 09:00, desde las 08:30». Sin etiqueta de franja, como antes. */
  function textoFranjaDomicilio() {
    if (!/^\d{2}:\d{2} a \d{2}:\d{2}$/.test(selectedFranja)) return `franja desde las ${selectedHorario}`;
    return selectedFranja.startsWith(selectedHorario)
      ? `franja de ${selectedFranja}`
      : `franja de ${selectedFranja}, desde las ${selectedHorario}`;
  }

  /* ─── Seleccionar horario (onChange del select) ──────────────── */
  function selectHorario(selectEl) {
    selectedHorario = selectEl.value;
    selectedFranja  = selectEl.selectedOptions && selectEl.selectedOptions[0] ? selectEl.selectedOptions[0].textContent.trim() : '';
    if ($('#horario-seleccionado')) $('#horario-seleccionado').value = selectedHorario;

    sede_id       = $('#sede_id')?.value;
    sedeName      = $('#sede')?.value;
    date_selected = $('#date-selected')?.value;
    tipo_servicio = Number($('#tipo_servicio')?.value);

    fechaAgenda = date_selected;
    horaAgenda  = selectedHorario;

    /* ── FIX: solo mostrar dirección si hay sede válida seleccionada ── */
    if (tipo_servicio === 3 && sede_id && sede_id !== '') {
      $('.select.domicilio-select')?.classList.remove('hidden');
      /* La dirección que el navegador guardó de otra compra no se usa sola: se muestra
         el botón para escogerla y "Agendar" espera a que el cliente la confirme.
         Si ya la confirmó en esta visita (p. ej. cambió de día), se vuelve a mostrar. */
      const addr = direccionEscogida();
      const ciudadAgenda = _getCiudadAgendamiento();
      _renderAddressChip(addr && (!ciudadAgenda || _ciudadCoincide(addr.city, ciudadAgenda)) ? addr : null);
    }

    $('div[finish-select]') && ($('div[finish-select]').style.display = '');

    beforeContinue();
  }

  /* ─── Tipo de horarios (mañana/tarde) ───────────────────────── */
  function selectThisHorarios(label) {
    // ── Requiere día seleccionado ──
    const diaSeleccionado = $('#day-selector .date-picker.col-1.selected');
    if (!diaSeleccionado) return;

    _resetChecks();
    const tipoHorarios = label.getAttribute('for');
    $$('.title-control .control label').forEach(l => l.classList.remove('selected'));
    label.classList.add('selected');
    fillHorarios(tipoHorarios);
  }

  /* ─── Continuar al pago ─────────────────────────────────────── */
  function beforeContinue() {
    const esDomicilio    = Number($('#tipo_servicio')?.value) === 3;
    const btn            = $('#continue-resumen');

    if (esDomicilio) {
      // Sin confirmar en esta visita no cuenta la dirección guardada en el navegador
      const selectedAddr = direccionEscogida();
      let tieneDireccionCompatible = false;

      if (selectedAddr) {
        const ciudadAgenda = _getCiudadAgendamiento();
        // Solo cuenta como válida si coincide con la ciudad del agendamiento
        tieneDireccionCompatible = !ciudadAgenda || _ciudadCoincide(selectedAddr.city, ciudadAgenda);
      }

      if (!tieneDireccionCompatible) {
        // Sin dirección compatible: oculta el botón y dice por qué
        btn?.classList.add('hidden');
        _avisoCiudad(!!selectedAddr);
      } else {
        _avisoCiudad(false);
        btn?.classList.remove('hidden');
        btn?.removeAttribute('disabled');
      }
    } else {
      _avisoCiudad(false);
      btn?.classList.remove('hidden');
      btn?.removeAttribute('disabled');
    }
  }

  /* H13: '2026-10-15' → 'jueves 15 de octubre' (la fecha se lee como hora local) */
  function fmtFechaLarga(iso) {
    const d = new Date(String(iso || '') + 'T00:00:00');
    if (isNaN(d.getTime())) return iso || '';
    return d.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });
  }

  /* H4: un doble clic en «Agendar» corría todo dos veces (dos avisos y dos eventos). Mientras
     una pulsación no termine, incluida su ventana de aviso, las demás no hacen nada. */
  let agendando = false;
  async function continueResumen(btn) {
    if (agendando) return;
    agendando = true;
    try { await _continueResumen(btn); } finally { agendando = false; }
  }

  async function _continueResumen(btn) {
    if (btn.getAttribute('disabled') === 'disabled') return;

    const metodoPago = $('#metodo_muestras')?.value;

    if (metodoPago === 'domicilio') {
      const codAthenea = $('#codAtheneaLocalidad')?.value;
      const cargos     = JSON.parse($('#products_cargos_domicilio')?.innerHTML || '{}');
      const key        = `P${codAthenea}`;

      if (key in cargos) {
        const product_cargo = cargos[key];

        /* Lee la dirección desde AddressManager (fuente única), solo si se confirmó en esta visita */
        const selectedAddr = direccionEscogida();
        const direccionLabel = selectedAddr
          ? [selectedAddr.label, selectedAddr.address1, selectedAddr.address2, selectedAddr.city, selectedAddr.province]
              .filter(Boolean).join(', ')
          : '';

        /* Sin dirección escogida no se agenda el domicilio (antes seguía con la dirección vacía) */
        if (!direccionLabel) {
          if (window.vtlMetrica) window.vtlMetrica('cita_error', { step: 2, detail: 'sin_direccion' });
          if (window.Swal) {
            await Swal.fire({ icon: 'info', title: 'Falta la dirección', text: 'Escoge o agrega la dirección donde te atenderemos.', confirmButtonColor: '#e31c79' });
          }
          openAddressModal();
          return;
        }

        let cargoAgregado = false;
        try {
          /* H14: primero se confirma; cerrar con la X no agrega el cargo ni guarda la cita */
          const r = await ventanaConfirma({
            titulo:       '¡Importante!',
            // B94: se nombra la franja que escogió el cliente; si sus primeras medias horas están
            // ocupadas, la cita queda más tarde dentro de ella y se dice desde qué hora
            texto:        `<p>Tu domicilio es el ${fmtFechaLarga(date_selected)}, ${textoFranjaDomicilio()}.</p>` +
                          '<p>El domiciliario llegará en cualquier momento de la franja horaria seleccionada.</p>',
            iconImg:      'https://cdn.shopify.com/s/files/1/0616/2137/1132/files/ICONO_domicilio.svg?v=1724705171',
            confirmLabel: 'Confirmar',
          });
          if (!r.isConfirmed) {
            if (window.vtlMetrica) window.vtlMetrica('cita_cancelada', { step: 2, detail: 'domicilio' });
            return;
          }

          /* N1: la cantidad sale de #times_domicilio, que CartView.refrescar() mantiene
             al día; el JSON de cargos se arma en Liquid al cargar y puede estar viejo */
          const cantidad = Math.max(1, Number($('#times_domicilio')?.value) || 1);
          /* M1: un cargo que quedó de un intento fallido se quita antes; si no,
             /cart/add.js suma la cantidad a la misma línea y se cobra doble */
          await _quitarCargos();
          await _addDomicilio({ items: [Object.assign({}, product_cargo, { quantity: cantidad })] });
          cargoAgregado = true;

          /* Actualiza atributos del carrito, incluyendo la dirección seleccionada */
          await _cartUpdate({
            _sede:                  sedeName,
            _sede_id:               sede_id,
            _date:                  date_selected,
            _horario:               selectedHorario,
            // La franja que vio el cliente: al pagar, verificarTurno busca otra media hora libre dentro de ella
            _franja:                selectedFranja,
            _tipo_servicio:         String(tipo_servicio),
            _direccion_domicilio:   direccionLabel,
          });

          if (window.vtlMetrica) window.vtlMetrica('cita_ok', { step: 2, detail: 'domicilio', data: { zona: String(codAthenea || '') } });
          /* Se agregó el cargo: se refrescan líneas y totales sin recargar la página */
          if (window.CartView) await window.CartView.refrescar();
          if (typeof window.vtlRenderCitaBadge === 'function') {
            window.vtlRenderCitaBadge();
          }
        } catch (e) {
          console.error(e);
          // M1: sin cita guardada no puede quedar el cargo (tiene candado y no se puede quitar a mano)
          if (cargoAgregado) await _quitarCargos().catch(err => console.error('[agenda] quitar cargo', err));
          if (window.vtlMetrica) window.vtlMetrica('cita_error', { step: 2, detail: 'domicilio' });
          _errorAgendar();
          if (window.CartView) await window.CartView.refrescar();
          if (typeof window.vtlRenderCitaBadge === 'function') window.vtlRenderCitaBadge();
        }
      } else {
        if (window.vtlMetrica) window.vtlMetrica('cita_error', { step: 2, detail: 'sin_domicilio_zona' });
        /* Sin cargo de domicilio para la localidad escogida: misma línea gráfica que la confirmación
           del domicilio (ícono de la marca, título y botón rosados) en lugar del ícono genérico */
        await ventanaConfirma({
          titulo:       'Domicilio no disponible',
          texto:        'Por ahora no tenemos servicio a domicilio en esta zona. Escoge otra localidad o agenda tu cita en una de nuestras sedes.',
          iconImg:      'https://cdn.shopify.com/s/files/1/0616/2137/1132/files/ICONO_domicilio.svg?v=1724705171',
          confirmLabel: 'Entendido',
        });
      }

    } else {
      try {
        const r = await ventanaConfirma({
          titulo:       '¡Importante!',
          texto:        `<p>Tu cita es el ${fmtFechaLarga(fechaAgenda)} a las ${horaAgenda} en la ${sedeAgenda}</p>`,
          iconImg:      'https://cdn.shopify.com/s/files/1/0616/2137/1132/files/Grupo_51587.svg?v=1724708566',
          confirmLabel: 'Confirmar',
        });
        /* H14: Swal no lanza error al cerrar con la X; sin confirmar no se guarda nada */
        if (!r.isConfirmed) {
          if (window.vtlMetrica) window.vtlMetrica('cita_cancelada', { step: 2, detail: 'sede' });
          return;
        }
        await _cartUpdate({
          _sede:          sedeName,
          _sede_id:       sede_id,
          _date:          date_selected,
          _horario:       selectedHorario,
          _franja:        '',
          _tipo_servicio: String(tipo_servicio),
          // B73: la ciudad de la sede, para precargar la facturación aunque se recargue en el paso 4
          _ciudad_sede:   _nombreCiudadAgendamiento(),
          // B86: una dirección de domicilio de antes no sigue en el carrito de una cita en sede
          _direccion_domicilio: '',
        });
        if (window.vtlMetrica) window.vtlMetrica('cita_ok', { step: 2, detail: 'sede', data: { sede: String(sede_id || '') } });
        /* N4: refrescar emite cart:updated, que vuelve a pintar el resumen del paso 4 */
        if (window.CartView) await window.CartView.refrescar();
        if (typeof window.vtlRenderCitaBadge === 'function') {
          window.vtlRenderCitaBadge();
        }
      } catch (e) {
        console.error(e);
        if (window.vtlMetrica) window.vtlMetrica('cita_error', { step: 2, detail: 'sede' });
        _errorAgendar();
      }
    }
  }

  /* ─── Calendario: meses ─────────────────────────────────────── */
  async function fillCalendarOptions(newDate) {
    _resetChecks();
    let monthK = newDate.getMonth();
    for (let i = 1; i <= 3; i++) {
      const btn = $(`#month-selector .date-picker.col-4[option="${i}"]`);
      if (!btn) continue;
      else btn.classList.remove('selected');
      btn.setAttribute('month-number', monthK);
      btn.innerHTML = `<b>${monthsName[monthK]}</b>`;
      btn.onclick = () => changeMonth(btn);
      monthK = (monthK + 1) % 12;
    }

    loading('hide');
    $('#month-selector')?.classList.remove('hidden');
  }

  /* ─── Calendario: días ──────────────────────────────────────── */
  function fillDaysCalendar(newDate) {
    loading('show');
    _soltarDiaEscogido();
    const fechasBloqueadas   = JSON.parse($('#datesBlocked')?.innerHTML      || '[]');
    const bloquesDiasSemanal = JSON.parse($('#bloquesDiasSemanal')?.innerHTML || '{}');

    const year    = newDate.getFullYear();
    const month   = newDate.getMonth();
    let dayK      = newDate.getDate();
    let weekDayK  = newDate.getDay();
    const lastDay = new Date(year, month + 1, 0).getDate();
    let minDate = null, lastDate = null;

    $('span.control-day.past')?.setAttribute('lastDate', lastDay);
    $('span.control-day.next')?.setAttribute('lastDate', lastDay);

    for (let i = 1; i <= 7; i++) {
      const btn = $(`#day-selector .date-picker.col-1[option="${i}"]`);
      if (!btn) continue;

      if (lastDay >= dayK) {
        if (weekDayK === 7) weekDayK = 0;
        const fullDate = `${year}-${pad(month+1,2)}-${pad(dayK,2)}`;
        btn.classList.remove('selected','hidden','disabled');
        btn.innerHTML = `<small>${weekdaysNameShort[weekDayK]}</small><b>${dayK}</b>`;
        btn.setAttribute('date', fullDate);

        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const manana = new Date(hoy);
        manana.setDate(hoy.getDate() + 1);
        const mananaStr = `${manana.getFullYear()}-${pad(manana.getMonth()+1,2)}-${pad(manana.getDate(),2)}`;

        if (fullDate < mananaStr)                 btn.classList.add('disabled');
        if (fechasBloqueadas.includes(fullDate))  btn.classList.add('disabled');
        if (weekDayK in bloquesDiasSemanal)       btn.classList.add('disabled');

        if (i === 1) minDate  = fullDate;
        // La semana que cruza de mes termina en el último día visible (antes quedaba en null
        // y la agenda llegaba vacía)
        lastDate = fullDate;
        dayK++; weekDayK++;
      } else {
        // Sin fecha: daysNextPage leía la de la semana anterior y la flecha se quedaba en el mismo mes
        btn.classList.add('hidden');
        btn.removeAttribute('date');
      }
    }

    // Al cruzar de mes con las flechas, el selector marca el mes que se está viendo
    const mesVisible = $(`#month-selector [month-number="${month}"]`);
    $$('#month-selector [month-number]').forEach(el => el.classList.toggle('selected', el === mesVisible));

    // Las flechas se apagan en los topes: la semana de mañana y el último día del tercer mes
    const flechaAnterior  = $('.control-day.past');
    const flechaSiguiente = $('.control-day.next');
    if (flechaAnterior)  flechaAnterior.disabled  = new Date(minDate + 'T00:00:00') <= _manana();
    if (flechaSiguiente) flechaSiguiente.disabled = new Date(lastDate + 'T00:00:00') >= _ultimoDiaAgendable();

    const sedeId   = $('#sede_id')?.value;
    const tipoServ = $('#tipo_servicio')?.value;
    const bandera  = $('#last_bandera')?.value;

    const fd = new FormData();
    fd.append('tipo_servicio', tipoServ);
    fd.append('sede_id', sedeId);
    fd.append('bandera', bandera);
    fd.append('minDate', minDate);
    fd.append('lastDate', lastDate);

    fetchConTimeout(`${middlewareBaseUrl}ajax/agendamientos.php`, { method: 'POST', body: fd })
      .then(data => {
        loading('hide');
        $('#day-selector')?.classList.remove('hidden');
        if ($('#blockDates')) $('#blockDates').innerHTML = JSON.stringify(data);
        if ($('#horario-container')) $('#horario-container').innerHTML = '';
      })
      .catch(err => {
        console.error('[agenda] agendamientos', err);
        errorAgenda(() => fillDaysCalendar(newDate));
      });
  }

  /* ─── Cambio de mes ─────────────────────────────────────────── */
  function changeMonth(element) {
    document.querySelectorAll('[month-number]')
      .forEach(el => { el.classList.remove('selected'); });
    element.classList.add('selected');
    _resetChecks();
    $('#agendamiento-hour-select')?.classList.add('hidden');
    $('#horario-container')?.classList.add('hidden');
    const monthSelected = Number(element.getAttribute('month-number'));
    let valDate = new Date();
    valDate.setHours(0,0,0,0);
    valDate.setDate(valDate.getDate() + 1);
    const actualMonth = valDate.getMonth();
    if (monthSelected !== actualMonth) {
      valDate.setDate(1);
      valDate.setMonth(monthSelected);
      if (monthSelected < actualMonth) {
        valDate.setFullYear(valDate.getFullYear() + 1);
      }
    }
    fillDaysCalendar(valDate);
  }

  /* ─── Precio de cargo domicilio ─────────────────────────────── */
  /* Devuelve true si la localidad no tiene cargo de domicilio (no se puede agendar).
     lugar: cómo se nombra en el aviso ('localidad' o, si se escogió sola, 'zona'). */
  function readCargoPrice(lugar = 'localidad') {
    const codAthenea = $('#codAtheneaLocalidad')?.value;
    const cargos     = JSON.parse($('#products_cargos_info')?.innerHTML || '{}');
    const key        = `P${codAthenea}`;
    if (key in cargos) {
      const price         = Number(cargos[key].price);
      const qty           = Number($('#times_domicilio')?.value || 1);
      const costoServicio = moneyFormat(price * qty);
      if (costoServicio && qty) {
        if ($('#cargo-servicio')) $('#cargo-servicio').innerHTML = `Costo de servicio a domicilio: <b>${costoServicio}</b>`;
      } else {
        if ($('#codAtheneaLocalidad')) $('#codAtheneaLocalidad').value = '';
      }
      return false;
    } else {
      $('#month-selector')?.classList.add('hidden');
      // V15-B6: antes «No es posible realizar el agendamiento de este producto…» con el ícono genérico
      _avisoSinDomicilio(lugar, true);
      removeCargoPrice();
      return true;
    }
  }

  function removeCargoPrice() {
    if ($('#cargo-servicio')) $('#cargo-servicio').innerHTML = '';
  }

  /* ─── Selects de agendamiento ───────────────────────────────── */
  async function readCalendar(idParametro, bandera, tipoServicio) {
    const fd = new FormData();
    fd.append('id_servicio', idParametro);
    fd.append('bandera', bandera);
    fd.append('tipo_servicio', tipoServicio);
    return fetchConTimeout(`${middlewareBaseUrl}ajax/calendar.php`, { method: 'POST', body: fd });
  }

  async function consultarCalendar(idParametro, bandera, tipoServicio, nombreSede) {
    const fd = new FormData();
    fd.append('sede_id', idParametro);
    fd.append('tipo_servicio', tipoServicio);
    fd.append('bandera', bandera);
    fd.append('sede', nombreSede);
    fd.append('metodo_muestras', $('#metodo_muestras')?.value);
    fd.append('sede_cp', $(`.agendamiento-select[bandera="CP"] select`)?.value);
    return fetchConTimeout(`${middlewareBaseUrl}ajax/consultarcalendar.php`, { method: 'POST', body: fd });
  }

  function printCalendarOptions(bandera, locations, metodoTitle) {
    _resetChecks();
    // H21: en domicilio el CTS llama "Sede" a la zona de la ciudad (Chapinero, Norte…)
    if ($('#metodo_muestras')?.value === 'domicilio' && /^sede$/i.test(String(metodoTitle).trim())) {
      metodoTitle = 'tu zona';
    }
    let html = `<option disabled selected>Selecciona ${metodoTitle}</option>`;
    locations.forEach(loc => {
      html += `<option idparametro="${loc.idParametro}" idfiltro="${loc.idFiltro}" nombresede="${loc.parametro.trim()}">${loc.parametro.trim()}</option>`;
    });
    const sel = $(`.agendamiento-select[bandera="${bandera}"] select`);
    if (sel) sel.innerHTML = html;
    $(`.agendamiento-select[bandera="${bandera}"]`)?.classList.remove('hidden');
    loading('hide');
  }

  // En hiddeInputs(), la parte del domicilio ya existe, solo agregar el reset del valor:
  function hiddeInputs(bandera) {
    ['CP','CS1','CS2','CS3'].forEach((b, idx, arr) => {
      if (arr.indexOf(bandera) <= idx) {
        $(`.agendamiento-select[bandera=${b}]`)?.classList.add('hidden');
      }
    });
    $('#month-selector')?.classList.add('hidden');
    $('div[finish-select]') && ($('div[finish-select]').style.display = 'none');
    $(`.agendamiento-select[bandera="localidades"]`)?.classList.add('hidden');
    if ($('#codAtheneaLocalidad')) $('#codAtheneaLocalidad').value = '';
    removeCargoPrice();

    /* ── FIX: ocultar Y resetear selector de dirección al cambiar ciudad/sede ── */
    const domicilioWrap = $('.select.domicilio-select');
    if (domicilioWrap) {
      domicilioWrap.classList.add('hidden');
      const addrSel = domicilioWrap.querySelector('#addressSelect');
      if (addrSel) addrSel.value = 'FALSO';   // ← reset del valor seleccionado
    }

    /* ── FIX: deshabilitar botón Agendar hasta que se complete el flujo ── */
    $('#continue-resumen')?.setAttribute('disabled', 'disabled');
  }

  async function changeAgendamientoSelect(select) {
    _resetChecks();
    $('#day-selector')?.classList.add('hidden');
    $('#agendamiento-hour-select')?.classList.add('hidden');
    $('#horario-container')?.classList.add('hidden');
    loading('show');

    const agendamientoSelect = select.parentElement;
    const bandera      = agendamientoSelect.getAttribute('bandera');
    const nextBandera  = agendamientoSelect.getAttribute('next-bandera');
    const tipoServicio = agendamientoSelect.getAttribute('tipo_servicio');
    const selectValue  = select.value;
    sedeAgenda = selectValue;

    hiddeInputs(nextBandera);

    const optionSelected = $(`.agendamiento-select[bandera="${bandera}"] select option[nombresede="${selectValue}"]`);
    const idParametro    = optionSelected?.getAttribute('idparametro');
    const nombreSede     = optionSelected?.getAttribute('nombresede');

    try {
      const res = await readCalendar(idParametro, nextBandera, tipoServicio);
      if (res.error !== 0) { errorAgenda(() => changeAgendamientoSelect(select)); return; }

      if (Object.keys(res.locations || {}).length > 0) {
        const locs = res.locations.filter(l => l.estado === true && l.idParametro != 2294);
        printCalendarOptions(nextBandera, locs, getMetodoTitle(nextBandera));
      } else {
        const calRes = await consultarCalendar(idParametro, bandera, tipoServicio, nombreSede);
        const esDomicilio = $('#metodo_muestras')?.value === 'domicilio';
        // V15-B6: a domicilio se avisa con el texto del domicilio y el cliente escoge otra ciudad o
        // zona en el mismo selector (vuelve a «Selecciona…»)
        const sinDomicilio = async (sinCargo) => {
          loading('hide');
          await _avisoSinDomicilio(_lugarDomicilio(bandera), sinCargo);
          select.selectedIndex = 0;
        };
        // Sin horarios o sin cupos: la misma medición que antes («sin_cupos», detalle «sede»)
        const sinHorarios = async () => {
          if (window.vtlMetrica) window.vtlMetrica('sin_cupos', { step: 2, detail: 'sede' });
          if (esDomicilio) { await sinDomicilio(false); return; }
          loading('hide');
          await ventanaConfirma({ titulo:'Sin horarios', texto:'Esta sede no tiene horarios disponibles por ahora. Prueba con otra sede o modalidad.', icon:'info', showConfirm:true, confirmLabel:'Entendido' });
        };

        if (_sinAgendaConfigurada(calRes)) {
          if (esDomicilio) { await sinDomicilio(false); return; }
          loading('hide');
          await ventanaConfirma({ titulo:'Info', texto:'No existen agendas configuradas para esta sede.', icon:'warning', showConfirm:true, confirmLabel:'Confirmar' });
          // El cliente escoge otra ciudad o sede en el mismo selector, que vuelve a «Selecciona…»,
          // como a domicilio (V15-B6). Antes resetAgendamiento() vaciaba y ocultaba todos los
          // selectores, también el de la ciudad, y la agenda quedaba en blanco sin forma de seguir.
          sedeAgenda = '';
          select.selectedIndex = 0;
          return;
        }

        if (calRes.error === 0 && calRes.bloquesHorario?.length > 0) {
          // V15-B1: con 0 cupos (o null) todas las horas salen ocupadas; antes se mostraba el
          // calendario y cada jornada decía «No quedan cupos… Prueba la semana siguiente» sin fin
          if (_cupos(calRes.cuposPorBloques) === 0) { await sinHorarios(); return; }
          const localidades = calRes.localidades || [];
          // V15-B6: a domicilio sin localidades «Agendar» no encuentra el cargo (codAthenea vacío) y
          // decía «Domicilio no disponible» después de escoger día y hora: se avisa desde aquí
          // (zonas Américas A y Suba en el CTS de pruebas)
          if (esDomicilio && !localidades.length && !_hayCargoDomicilio('')) { await sinDomicilio(true); return; }

          _populateHiddenInputs(calRes, tipoServicio, bandera, nombreSede);
          const newDate = new Date();
          newDate.setHours(0,0,0,0);
          newDate.setDate(newDate.getDate() + 1);

          switch (localidades.length) {
            case 0:  fillCalendarOptions(newDate); break;
            case 1:
              if ($('#codAtheneaLocalidad')) $('#codAtheneaLocalidad').value = localidades[0].codAthenea;
              // V15-B6: sin cargo para la única localidad no se muestra el calendario (antes
              // readCargoPrice lo ocultaba y fillCalendarOptions lo volvía a mostrar)
              if (readCargoPrice(_lugarDomicilio(bandera))) { loading('hide'); select.selectedIndex = 0; break; }
              fillCalendarOptions(newDate);
              break;
            default: getLocalidades(localidades); break;
          }
        } else if (calRes.error !== 0) {
          errorAgenda(() => changeAgendamientoSelect(select));
        } else {
          await sinHorarios();
        }
      }
    } catch(e) {
      console.error('changeAgendamientoSelect error', e);
      errorAgenda(() => changeAgendamientoSelect(select));
    }
  }

  function _populateHiddenInputs(calRes, tipoServicio, bandera, nombreSede) {
    const set     = (id, val) => { if ($(id)) $(id).value = val; };
    const setJSON = (id, val) => { if ($(id)) $(id).innerHTML = JSON.stringify(val); };
    set('#tipo_servicio', tipoServicio);
    set('#last_bandera', bandera);
    set('#sede', nombreSede);
    set('#sede_id', calRes.sede_id);
    setJSON('#datesBlocked', calRes.fechasBloqueadas);
    setJSON('#almuerzoPorDiaSemanal', calRes.horariosAlmuerzoPorDiaSemanal);
    setJSON('#bloquesHorario', calRes.horariosPorDiaSemanal);
    setJSON('#bloquesDiasSemanal', calRes.bloquesDiasSemanal);
    setJSON('#cuposPorBloques', _cupos(calRes.cuposPorBloques));
  }

  async function calendarioWs(querySelector, idParametro) {
    const el = $(querySelector);
    if (!el) return;
    const tipoServicio = el.getAttribute('tipo_servicio');
    const bandera      = el.getAttribute('bandera');
    const nextBandera  = el.getAttribute('next-bandera');

    const res = await readCalendar(idParametro, bandera, tipoServicio);
    if (res.error !== 0) return;

    const locs = Object.values(res.locations || {});
    if (locs.length > 1) {
      const filtrados = locs.filter(l => l.estado === true && l.idParametro != 70);
      printCalendarOptions(bandera, filtrados, getMetodoTitle(bandera));
    } else if (locs.length === 1) {
      calendarioWs(`.agendamiento-select[bandera="${nextBandera}"]`, locs[0].idParametro);
    }
  }

  function applyIdToSelects(id) {
    $$('.agendamiento-select').forEach(s => s.setAttribute('tipo_servicio', id));
  }

  function selectMetodoMuestra(metodo, agendamientoId) {
    window.dataMetodoMuestra = {
      metodo: metodo,
      agendamientoId: agendamientoId,
    };
    resetAgendamiento();
    loading('show');
    if ($('#metodo_muestras')) $('#metodo_muestras').value = metodo;
    if (window.vtlMetrica) window.vtlMetrica('elegir_modalidad', { step: 2, detail: metodo === 'sedes' ? 'sede' : metodo });

    const agendamientoTitle = metodo === 'domicilio'
      ? 'Agenda tu cita a Domicilio'
      : 'Agenda tu cita en Bogotá';

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _metodo: metodo } }),
    }).then(() => {
      applyIdToSelects(agendamientoId);
      const titleEl = $('.agendamiento-title');
      if (titleEl) titleEl.innerHTML = agendamientoTitle;
      calendarioWs(`.agendamiento-select[bandera="CP"]`, agendamientoId);
    });
  }

  function cleanCart() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: {
        _sede:'', _metodo:'', _horario:'', _date:'', _franja:'',
        _sede_id:'', _metodoCitas:'', _tipo_servicio:'',
        _direccion_domicilio:'', _ciudad_sede:'',
      }}),
    });
  }

  /* ─── Localidades (domicilio) ───────────────────────────────── */
  function getLocalidades(localidades) {
    loading('hide');
    let html = '<option>Selecciona la Localidad</option>';
    localidades.forEach(loc => {
      html += `<option codathenea="${loc.codAthenea}" nombrelocalidad="${loc.nombreLocalidad.trim()}">${loc.nombreLocalidad.trim()}</option>`;
    });
    const sel = $(`.agendamiento-select[bandera="localidades"] select`);
    if (sel) sel.innerHTML = html;
    $(`.agendamiento-select[bandera="localidades"]`)?.classList.remove('hidden');
  }

  function selectLocalidad(selectEl) {
    const val        = selectEl.value;
    const opt        = $(`[nombreLocalidad="${val}"]`);
    const codAthenea = opt?.getAttribute('codathenea');
    if ($('#codAtheneaLocalidad')) $('#codAtheneaLocalidad').value = codAthenea;
    if (readCargoPrice()) return;

    // ── Limpia selección previa de mes, día y horario ──────────────
    $$('#month-selector .date-picker').forEach(el => el.classList.remove('selected'));
    $('#day-selector')?.classList.add('hidden');
    $$('#day-selector .date-picker').forEach(el => el.classList.remove('selected'));
    $('#agendamiento-hour-select')?.classList.add('hidden');
    if ($('#horario-container'))      $('#horario-container').innerHTML      = '';
    if ($('#horario-container-domi')) $('#horario-container-domi').innerHTML = '';
    if ($('#date-selected'))          $('#date-selected').value              = '';
    if ($('#horario-seleccionado'))   $('#horario-seleccionado').value       = '';
    $('div[finish-select]') && ($('div[finish-select]').style.display = 'none');
    $('#continue-resumen')?.setAttribute('disabled', 'disabled');

    // Oculta y limpia la sección de domicilio al cambiar localidad
    const domicilioWrap = $('#sched-domicilio-wrap');
    domicilioWrap?.classList.add('hidden');
    _renderAddressChip(null);
    // ──────────────────────────────────────────────────────────────

    const d = new Date();
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 1);
    fillCalendarOptions(d);
  }

  /* ─── Helpers internos ──────────────────────────────────────── */
  function _resetChecks() {
    const p = $('[name="accept-politica"]');
    const i = $('[name="accept-info"]');
    if (p) p.checked = false;
    if (i) i.checked = false;
  }

  /* N11: las dos escrituras fallan con error si Shopify no las acepta (p. ej. 422),
     así continueResumen avisa en lugar de dejar la cita a medias. */
  /* V15-B12: ms opcional, tiempo límite de la petición (se usa al verificar el turno antes de pagar) */
  async function _cartUpdate(attributes, ms) {
    const ctrl  = ms ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), ms) : null;
    try {
      const r = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
        signal: ctrl ? ctrl.signal : undefined,
      });
      if (!r.ok) throw new Error('cart/update.js ' + r.status);
      return r;
    } finally {
      clearTimeout(timer);
    }
  }

  async function _addDomicilio(product_cargo) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(product_cargo),
    });
    if (!r.ok) throw new Error('cart/add.js ' + r.status);
    return r;
  }

  /* M1: quita del carrito todas las líneas del cargo de domicilio */
  async function _quitarCargos() {
    const cart = await fetch('/cart.js').then(r => r.json());
    const cargos = (cart.items || []).filter(item =>
      String((item.properties || {})._product_type || item.product_type || '').trim() === 'Cargo');
    for (const item of cargos) {
      const r = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: item.key, quantity: 0 }),
      });
      if (!r.ok) throw new Error('cart/change.js ' + r.status);
    }
  }

  function _errorAgendar() {
    ventanaConfirma({
      titulo:      'No pudimos guardar tu cita',
      texto:       'Revisa tu conexión e intenta agendar de nuevo.',
      icon:        'error',
      confirmLabel:'Entendido',
    });
  }

  /* ─── SweetAlert2 wrapper unificado ────────────────────────── */
  function ventanaConfirma({
    titulo        = '',
    texto         = '',
    icon          = '',
    showConfirm   = true,
    showCancel    = false,
    confirmLabel  = 'Confirmar',
    iconColor     = '#e71578',
    btnColor      = '#e71578',
    iconImg       = '',
  } = {}) {
    return Swal.fire({
      icon: icon || undefined,
      html: `
        <div style="display:flex;flex-direction:column;align-items:center;text-align:center">
          ${iconImg && !icon ? `<img src="${iconImg}" style="margin:1.5rem auto;max-height:80px">` : ''}
          <strong style="font-size:18px;color:${iconColor};margin-bottom:8px">${titulo}</strong>
          <div>${texto}</div>
        </div>`,
      showConfirmButton:  showConfirm,
      showCancelButton:   showCancel,
      showCloseButton:    true,
      allowOutsideClick:  false,
      confirmButtonText:  confirmLabel,
      cancelButtonText:   'Cancelar',
      confirmButtonColor: btnColor,
    });
  }

  /* ─── Reset completo del agendamiento ────────────────────────── */
  function resetAgendamiento() {
    fechaAgenda = '';
    horaAgenda  = '';
    sedeAgenda  = '';

    ['#sede_id','#sede','#last_bandera','#tipo_servicio',
     '#codAtheneaLocalidad','#date-selected','#horario-seleccionado']
      .forEach(id => { if ($(id)) $(id).value = ''; });

    const jsonDefaults = {
      '#datesBlocked':          '[]',
      '#almuerzoPorDiaSemanal': '{}',
      '#bloquesHorario':        '{}',
      '#blockDates':            '[]',
      '#bloquesDiasSemanal':    '{}',
      '#cuposPorBloques':       '1',
    };
    Object.entries(jsonDefaults).forEach(([id, val]) => {
      if ($(id)) $(id).innerHTML = val;
    });

    $$('.agendamiento-select').forEach(el => {
      el.classList.add('hidden');
      const sel = el.querySelector('select');
      if (sel) sel.innerHTML = '';
    });

    $('#month-selector')?.classList.add('hidden');
    $('#day-selector')?.classList.add('hidden');
    $('#agendamiento-hour-select')?.classList.add('hidden');

    if ($('#horario-container'))      $('#horario-container').innerHTML      = '';
    if ($('#horario-container-domi')) $('#horario-container-domi').innerHTML = '';

    $$('.title-control .control label').forEach(l => l.classList.remove('selected'));
    $$('#day-selector .date-picker').forEach(d => d.classList.remove('selected'));

    // Oculta la sección de domicilio y limpia el chip
    const domicilioWrap = $('#sched-domicilio-wrap');
    domicilioWrap?.classList.add('hidden');
    _renderAddressChip(null);

    removeCargoPrice();

    const footer = $('div[finish-select]');
    const btnCta = $('#continue-resumen');
    if (footer) footer.style.display = 'none';
    if (btnCta) btnCta.setAttribute('disabled', 'disabled');

    _resetChecks();
    loading('hide');

    const title = $('.agendamiento-title');
    if (title) title.innerHTML = '';
  }

  /* ─── Turno al pagar ────────────────────────────────────────── */
  /* La web no aparta el turno al escogerlo: si mientras tanto lo toma otro cliente o un asesor
     en el CTS, la venta se creaba sin cita (pedidos #1018 y #1078 de pruebas). Antes de ir al
     pago se vuelven a pedir los turnos tomados y los cupos de la agenda del carrito.
     V15-B1: los cupos se cuentan como en _opcionesHorarios (_cupos: 0 o null = sin cupos, el turno
     queda ocupado); antes `|| 1` daba «libre» en una agenda sin cupos configurados.
     En domicilio, si esa media hora ya se ocupó pero queda otra libre en la misma franja, la cita
     pasa a esa (el domiciliario llega en cualquier momento de la franja).
     atributos (opcional): los del carrito que el paso 4 ya leyó; sin ellos se lee /cart.js.
     V15-B12: cada petición tiene tiempo límite (TURNO_TIMEOUT_MS); si vence, como cualquier
     falla, se deja pagar ('sin_verificar').
     Devuelve { estado: 'libre' | 'movido' | 'ocupado' | 'sin_verificar' }; con 'ocupado', además
     motivo: 'tomado' | 'noche' (regla de las 6:00 p. m.) | 'dia_bloqueado' (festivo o cierre) |
     'sin_guardar' (Shopify no guardó el cambio). Con 'movido', motivo es la causa de la hora original. */
  async function verificarTurno(atributos) {
    let a = atributos;
    if (!a) {
      try {
        a = (await fetchConTimeout('/cart.js', {}, TURNO_TIMEOUT_MS)).attributes || {};
      } catch (e) {
        return { estado: 'sin_verificar' };
      }
    }
    const fecha  = String(a._date || '').trim();
    const hora   = String(a._horario || '').trim();
    const agenda = String(a._sede_id || '').trim();
    if (!fecha || !hora || !agenda) return { estado: 'sin_verificar' };

    const fd = new FormData();
    fd.append('sede_id', agenda);
    fd.append('tipo_servicio', String(a._tipo_servicio || ''));
    // Sin «CP»: _sede_id ya es la agenda y consultarcalendar.php no la vuelve a convertir;
    // sin modalidad no consulta localidades, que aquí no hacen falta
    fd.append('bandera', '');
    fd.append('sede', String(a._sede || ''));
    fd.append('metodo_muestras', '');
    fd.append('sede_cp', '');

    let res;
    try {
      res = await fetchConTimeout(`${middlewareBaseUrl}ajax/consultarcalendar.php`, { method: 'POST', body: fd }, TURNO_TIMEOUT_MS);
    } catch (e) {
      console.warn('[agenda] verificarTurno', e);
      return { estado: 'sin_verificar' };
    }
    // Sin cuposPorBloques la respuesta está incompleta: como una falla, se deja pagar
    if (!res || res.error !== 0 || !Array.isArray(res.fechasBloqueadas) || !('cuposPorBloques' in res)) return { estado: 'sin_verificar' };

    const tomados = res.fechasBloqueadas;
    const cupos   = _cupos(res.cuposPorBloques);
    // Día bloqueado completo (festivo o cierre): el middleware lo manda solo con la fecha
    if (tomados.includes(fecha)) return { estado: 'ocupado', motivo: 'dia_bloqueado' };
    const libre = h => tomados.filter(f => f === `${fecha} ${h}`).length < cupos;
    // B4 (/probar 26-10): a domicilio, la hora guardada también respeta la regla nocturna (se escogió
    // antes de las 6:00 p. m. y se paga después)
    const deNoche = h => String(a._tipo_servicio || '').trim() === '3' && _bloqueadaDeNoche(fecha, h);
    if (libre(hora) && !deNoche(hora)) return { estado: 'libre' };
    // B1 (/probar 26-10 16:26): motivo para el aviso del paso 4 (tomado, regla nocturna o Shopify no guardó)
    const causa = libre(hora) ? 'noche' : 'tomado';
    let motivo = causa;

    // V15-A1: la franja solo vale si la hora guardada cae dentro de ella (inicio <= hora < fin). Una
    // _franja que quedó de otra cita (p. ej. la cita se cambió en el chat) movía la cita a otra jornada
    const franja = _franjaDeLaCita(a);
    const limiteMover = Date.now() + TURNO_MOVER_MAX_MS;
    if (franja) {
      mover: for (let m = franja.inicio; m < franja.fin; m += 30) {
        const h = `${pad(Math.floor(m / 60), 2)}:${pad(m % 60, 2)}`;
        // B4 (/probar 26-10): la media hora nueva respeta la misma regla nocturna que la lista de franjas
        if (!libre(h) || deNoche(h)) continue;
        // B1 (/probar 26-10): había cupo; si Shopify no guarda el cambio se reintenta una vez y, si
        // vuelve a fallar, se pasa a la siguiente media hora libre. Sin moverla queda 'ocupado' (pagar
        // con la media hora tomada dejaba la venta sin cita)
        for (let intento = 0; intento < 2; intento++) {
          const resta = limiteMover - Date.now();
          if (resta < 1000) { motivo = 'sin_guardar'; break mover; }
          try {
            await _cartUpdate({ _horario: h }, Math.min(TURNO_TIMEOUT_MS, resta));
            return { estado: 'movido', horario: h, motivo: causa };
          } catch (e) {
            console.warn('[agenda] verificarTurno: no se pudo mover la cita', e);
            motivo = 'sin_guardar';
          }
        }
      }
    }
    return { estado: 'ocupado', motivo };
  }

  /* Domicilio: después de las 6:00 p. m. (o antes de las 7:00 a. m.) no se ofrecen para el día
     siguiente las horas de 6:00 a 7:59; no alcanza a programarse la visita. */
  function _bloqueadaDeNoche(fecha, h) {
    const ahora = new Date().getHours();
    if (!(ahora >= 18 || ahora < 7)) return false;
    const manana = new Date();
    manana.setDate(manana.getDate() + 1);
    const mananaStr = `${manana.getFullYear()}-${pad(manana.getMonth()+1,2)}-${pad(manana.getDate(),2)}`;
    if (mananaStr !== fecha) return false;
    const hora = parseInt(String(h).split(':')[0], 10);
    return hora >= 6 && hora < 8;
  }

  /* V15-A1: { inicio, fin } en minutos de la franja de un domicilio («06:00 a 08:00» en _franja), solo
     si _horario cae dentro de ella; si no, null. La misma regla usa vtlTextoHoraCita
     (cart-service-schedule.liquid) para mostrar la franja en la píldora y en el paso 4. */
  function _franjaDeLaCita(a) {
    if (String(a._tipo_servicio || '').trim() !== '3') return null;
    const f = /^(\d{1,2}:\d{2}) a (\d{1,2}:\d{2})$/.exec(String(a._franja || '').trim());
    if (!f) return null;
    const inicio = _minutos(f[1]);
    const fin    = _minutos(f[2]);
    const hora   = _minutos(a._horario);
    return inicio <= hora && hora < fin ? { inicio, fin } : null;
  }

  /* ─── Init ──────────────────────────────────────────────────── */
  function init() {
    initAccordion();

    /*
     * Suscribe CartAgenda al evento de selección de AddressManager.
     *
     * FIX: Ya NO se pre-renderiza el chip al cargar la página aunque
     * ya haya una dirección seleccionada. El chip solo aparece cuando
     * selectHorario() revela el wrap (#sched-domicilio-wrap), y
     * únicamente si la ciudad de la dirección coincide con la del
     * agendamiento activo.
     */
    if (typeof window.AddressManager !== 'undefined') {
      window.AddressManager.onSelect(onAddressSelect);
    } else {
      // AddressManager aún no cargó; reintenta 1 vez tras un tick
      setTimeout(() => {
        if (typeof window.AddressManager !== 'undefined') {
          window.AddressManager.onSelect(onAddressSelect);
        }
      }, 200);
    }
  }

  /* ── Acordeón manual → sincroniza StepperAPI.current ── */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.cart-accordion__trigger');
    if (!trigger) return;

    var item     = trigger.closest('.cart-accordion__item');
    var accIndex = parseInt(item?.dataset.accordionIndex);

    // Solo sincroniza, no abre — el AccordionAPI nativo ya lo hace
    var stepMap = { 1: 1, 2: 3, 3: 4 }; // accordionIndex → stepperStep
    var step    = stepMap[accIndex];

    if (step && typeof StepperAPI !== 'undefined') {
      StepperAPI.current = step - 1; // deja current en el paso anterior para que goTo(step) funcione
    }
  });

  document.addEventListener('DOMContentLoaded', init);

  /* ─── Exposición pública ────────────────────────────────────── */
  window.CartAgenda = {
    /* Calendario */
    daysNextPage,
    daysBackPage,
    selectDate,
    fillHorarios,
    selectHorario,
    selectThisHorarios,
    fillCalendarOptions,
    fillDaysCalendar,
    changeMonth,
    /* Selects */
    changeAgendamientoSelect,
    calendarioWs,
    printCalendarOptions,
    /* Precios */
    readCargoPrice,
    removeCargoPrice,
    /* Flujo */
    beforeContinue,
    continueResumen,
    selectMetodoMuestra,
    resetAgendamiento,
    cleanCart,
    verificarTurno,
    /* Localidades */
    getLocalidades,
    selectLocalidad,
    /* Direcciones (delegado a AddressManager) */
    openAddressModal,
    onAddressSelect,
    /* Utilidades */
    ventanaConfirma,
    _cartUpdate,
  };
})();